import { notNull } from "./helpers";
import {
  type DeviceLocalization,
  getLocalizationRegion,
  type LocalizationRegion,
  type LocalizationSignals,
} from "./localizationRegion";
import type { AudioLanguage } from "./types";

// Annotated Record<string, string> so call sites can index with arbitrary
// strings (browser locales); satisfies keeps it exhaustive over AudioLanguage.
export const LANGUAGE_FLAGS: Record<string, string> = {
  en: "🇺🇸",
  "en-medical": "🇺🇸",
  fr: "🇫🇷",
  "fr-CA": "🇨🇦",
  de: "🇩🇪",
  "de-CH": "🇨🇭",
  it: "🇮🇹",
  pt: "🇵🇹",
  "pt-BR": "🇧🇷",
  es: "🇪🇸",
  zh: "🇨🇳",
  "zh-TW": "🇹🇼",
  "zh-HK": "🇭🇰",
  af: "🇿🇦",
  sq: "🇦🇱",
  am: "🇪🇹",
  ar: "🇸🇦",
  hy: "🇦🇲",
  as: "🇮🇳",
  ast: "🇪🇸",
  az: "🇦🇿",
  ba: "🇷🇺",
  eu: "🇪🇸",
  be: "🇧🇾",
  bn: "🇧🇩",
  bs: "🇧🇦",
  br: "🇫🇷",
  bg: "🇧🇬",
  my: "🇲🇲",
  ca: "🇪🇸",
  ceb: "🇵🇭",
  ny: "🇲🇼",
  hr: "🇭🇷",
  cs: "🇨🇿",
  da: "🇩🇰",
  nl: "🇳🇱",
  et: "🇪🇪",
  fo: "🇫🇴",
  fi: "🇫🇮",
  "nl-BE": "🇧🇪",
  // Fulah is spoken across West Africa with no single country; generic fallback.
  ff: "🌐",
  gl: "🇪🇸",
  lg: "🇺🇬",
  ka: "🇬🇪",
  el: "🇬🇷",
  gu: "🇮🇳",
  ht: "🇭🇹",
  ha: "🇳🇬",
  haw: "🇺🇸",
  he: "🇮🇱",
  hi: "🇮🇳",
  hu: "🇭🇺",
  is: "🇮🇸",
  ig: "🇳🇬",
  id: "🇮🇩",
  ga: "🇮🇪",
  ja: "🇯🇵",
  jw: "🇮🇩",
  kea: "🇨🇻",
  kn: "🇮🇳",
  kk: "🇰🇿",
  km: "🇰🇭",
  ko: "🇰🇷",
  // Kurdish has no country flag emoji; generic fallback.
  ku: "🌐",
  ky: "🇰🇬",
  lo: "🇱🇦",
  la: "🇻🇦",
  lv: "🇱🇻",
  ln: "🇨🇩",
  lt: "🇱🇹",
  // Luo is spoken across Kenya/Tanzania with no single country; generic fallback.
  luo: "🌐",
  lb: "🇱🇺",
  mk: "🇲🇰",
  mg: "🇲🇬",
  ms: "🇲🇾",
  ml: "🇮🇳",
  mt: "🇲🇹",
  mi: "🇳🇿",
  mr: "🇮🇳",
  mn: "🇲🇳",
  ne: "🇳🇵",
  nso: "🇿🇦",
  no: "🇳🇴",
  nn: "🇳🇴",
  oc: "🇫🇷",
  or: "🇮🇳",
  pa: "🇮🇳",
  ps: "🇦🇫",
  fa: "🇮🇷",
  pl: "🇵🇱",
  ro: "🇷🇴",
  ru: "🇷🇺",
  sa: "🇮🇳",
  sr: "🇷🇸",
  sn: "🇿🇼",
  sd: "🇵🇰",
  si: "🇱🇰",
  sk: "🇸🇰",
  sl: "🇸🇮",
  so: "🇸🇴",
  su: "🇮🇩",
  sw: "🇰🇪",
  sv: "🇸🇪",
  tl: "🇵🇭",
  tg: "🇹🇯",
  ta: "🇮🇳",
  tt: "🇷🇺",
  te: "🇮🇳",
  th: "🇹🇭",
  // No globally accepted flag emoji for Tibetan; use the generic fallback.
  bo: "🌐",
  tr: "🇹🇷",
  tk: "🇹🇲",
  uk: "🇺🇦",
  umb: "🇦🇴",
  ur: "🇵🇰",
  uz: "🇺🇿",
  vi: "🇻🇳",
  cy: "🏴󠁧󠁢󠁷󠁬󠁳󠁿",
  wo: "🇸🇳",
  xh: "🇿🇦",
  yi: "🇮🇱",
  yo: "🇳🇬",
  zu: "🇿🇦",
} satisfies Record<AudioLanguage, string>;

// Display-only flag swaps for viewers in a localization region.
const REGIONAL_LANGUAGE_FLAGS: Record<
  LocalizationRegion,
  Partial<Record<string, string>>
> = {
  europe: { en: "🇬🇧", "en-medical": "🇬🇧" },
};

export const getLocaleRegion = (locale: string) => {
  try {
    return new Intl.Locale(locale).region;
  } catch {
    return undefined;
  }
};

const getRegionalLanguageFlag = ({
  language,
  region,
}: {
  language: string;
  region: LocalizationRegion | null;
}) => (region === null ? undefined : REGIONAL_LANGUAGE_FLAGS[region][language]);

export const getLanguageFlag = ({
  language,
  localization = {},
}: {
  language: string;
  localization?: LocalizationSignals;
}) =>
  getRegionalLanguageFlag({
    language,
    region: getLocalizationRegion(localization),
  }) ??
  LANGUAGE_FLAGS[language] ??
  "🌐";

// Server-side proxy for navigator.languages, so first paint matches the device tier.
export const getAcceptLanguageRegions = (
  header: string | null
): readonly string[] =>
  (header ?? "")
    .split(",")
    .map((part) => part.replace(/;.*$/, "").trim())
    .filter((locale) => locale !== "" && locale !== "*")
    .map(getLocaleRegion)
    .filter(notNull);

export const getBrowserLanguageFlagLocalization = (): DeviceLocalization =>
  typeof navigator === "undefined"
    ? {}
    : {
        locales: navigator.languages ?? [navigator.language],
        localeRegions: (navigator.languages ?? [navigator.language])
          .map(getLocaleRegion)
          .filter(notNull),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
